<script setup lang="ts">
  // POS V2 command bar: mode tabs · search · view toggle · page tools (caja
  // chip, dropdowns via #tools) · New order. Single chrome row replacing the
  // old toolbar/dropdown-strip/header-card stack.
  import { computed } from 'vue';
  import { OrderType } from '@/enums/OrderEnums';
  import { usePosNewOrder } from '@/composition/use-pos-new-order';
  import { usePosTablesView } from '@/composition/use-pos-tables-view';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import PosUiInput from '@/components/pos-v2/ui/PosUiInput.vue';
  import PosV2ModeTabs from './PosV2ModeTabs.vue';

  const props = defineProps<{
    mode: OrderType;
    search: string;
  }>();

  const emit = defineEmits<{
    (e: 'update:mode', mode: OrderType): void;
    (e: 'update:search', search: string): void;
  }>();

  const restaurantStore = useRestaurantStore();
  const tablesView = usePosTablesView();
  const { isWhatsAppMode, navigateToNewOrder } = usePosNewOrder();

  const showViewToggle = computed(() => props.mode === OrderType.Seat);

  function handleNewOrder() {
    navigateToNewOrder(props.mode ?? OrderType.Seat);
  }
</script>

<template>
  <div class="pos-v2-header">
    <PosV2ModeTabs
      :value="mode"
      class="header-tabs"
      @input="(m) => emit('update:mode', m)"
    />

    <div class="header-search">
      <PosUiInput
        :value="search"
        icon="magnify"
        size="md"
        clearable
        :placeholder="$t('posV2.searchOrdersPlaceholder')"
        @input="(v) => emit('update:search', v)"
      />
    </div>

    <div v-if="showViewToggle" class="view-toggle">
      <button
        type="button"
        class="toggle-button"
        :class="{ active: tablesView.effectiveView.value === 'plan' }"
        @click="tablesView.setView('plan')"
      >
        <PosUiIcon name="floor-plan" class="toggle-icon" />
        <span class="toggle-label">{{ $t('posTables.viewPlan') }}</span>
      </button>
      <button
        type="button"
        class="toggle-button"
        :class="{ active: tablesView.effectiveView.value === 'list' }"
        @click="tablesView.setView('list')"
      >
        <PosUiIcon name="view-grid-outline" class="toggle-icon" />
        <span class="toggle-label">{{ $t('posTables.viewList') }}</span>
      </button>
    </div>

    <div class="header-tools">
      <slot name="tools" />
    </div>

    <PosUiButton
      v-if="!isWhatsAppMode"
      color="primary"
      variant="solid"
      size="md"
      icon="plus"
      class="new-order-button"
      @click="handleNewOrder"
    >
      {{ $t('posV2.newOrder') }}
    </PosUiButton>
    <div v-else class="whatsapp-warning">
      <PosUiIcon name="information" class="warning-icon" />
      <span class="warning-text">
        {{ $t('posV2.whatsAppCreationDisabled') }}
      </span>
      <router-link
        class="warning-link"
        :to="`/${restaurantStore.restaurant.stringId}/settings?tab=orders`"
      >
        <PosUiIcon name="cog" class="warning-link-icon" />
        {{ $t('posV2.configure') }}
      </router-link>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-v2-header {
    @apply tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-b tw-border-neutral-200 tw-bg-white tw-px-3 tw-py-2;

    .header-tabs {
      @apply tw-order-1 tw-w-full sm:tw-order-none sm:tw-w-auto sm:tw-flex-none;
    }

    .header-search {
      @apply tw-order-2 tw-min-w-[150px] tw-max-w-md tw-flex-1 sm:tw-order-none;
    }

    .view-toggle {
      @apply tw-order-2 tw-flex tw-flex-none tw-overflow-hidden tw-rounded-lg tw-border tw-border-neutral-300 sm:tw-order-none;

      .toggle-button {
        @apply tw-flex tw-min-h-[40px] tw-items-center tw-gap-1.5 tw-px-3 tw-text-sm tw-font-medium tw-text-neutral-500 tw-transition-colors;

        &:hover {
          @apply tw-bg-neutral-50 tw-text-neutral-700;
        }

        &.active {
          @apply tw-bg-neutral-100 tw-text-neutral-900;
        }

        & + .toggle-button {
          @apply tw-border-l tw-border-neutral-300;
        }

        .toggle-icon {
          @apply tw-text-lg;
        }

        .toggle-label {
          @apply tw-hidden xl:tw-inline;
        }
      }
    }

    .header-tools {
      @apply tw-order-2 tw-ml-auto tw-flex tw-flex-none tw-items-center tw-gap-1.5 sm:tw-order-none;

      &:empty {
        @apply tw-hidden;
      }
    }

    .new-order-button {
      // On mobile the page shows a sticky bottom bar instead.
      @apply tw-order-2 tw-hidden tw-flex-none md:tw-order-none md:tw-inline-flex;
    }

    .whatsapp-warning {
      @apply tw-order-2 tw-flex tw-flex-none tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-amber-200 tw-bg-amber-50 tw-px-3 tw-py-2 md:tw-order-none;

      .warning-icon {
        @apply tw-flex-none tw-text-lg tw-text-amber-500;
      }

      .warning-text {
        @apply tw-text-xs tw-font-medium tw-text-amber-800;
      }

      .warning-link {
        @apply tw-flex tw-flex-none tw-items-center tw-gap-0.5 tw-text-xs tw-font-semibold tw-text-amber-800 hover:tw-underline;

        .warning-link-icon {
          @apply tw-text-sm;
        }
      }
    }
  }
</style>
